// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import {
  getViewportInfo,
  getIconPath,
  getImageFilePath,
  parseBoolean,
  getAPIConfig,
  getAspectRatio,
  removeExtension,
} from '@tcp/core/src/utils';
import {
  getHandlerArgs,
  visibility,
} from '@tcp/web/src/components/common/molecules/PageVisibility/utils.web';
import {
  getVideoUrls,
  getPosterUrls,
  findPosterImage,
  cleanUpUrl,
  getAssetHostVal,
  isAbsoluteUrl,
} from '../utils/DamImageUtils';
import ImageSource from './ImageSource.view';
import { StyledVideo, getVideoStyle, StyledImage } from '../DamImage.styles';

const useIntersectionObserver = (options = {}, defaultVisibility = false) => {
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(defaultVisibility);
  const callbackFn = (entries) => {
    const [entry] = entries;
    setIsVisible(entry.isIntersecting);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(callbackFn, options);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, [containerRef?.current?.getBoundingClientRect(), options]);

  return [containerRef, isVisible];
};

const onScrollAnimationVideo = (id, tag) => {
  const parentEl = document.querySelector(id);
  const videoTag = document.querySelector(tag);
  const scrollVideo = () => {
    if (videoTag && videoTag.duration) {
      const distanceFromTop = window.scrollY + parentEl.getBoundingClientRect().top;
      const rawPercentScrolled =
        (window.scrollY - distanceFromTop) / (parentEl.scrollHeight - window.innerHeight);
      const percentScrolled = Math.min(Math.max(rawPercentScrolled, 0), 1);

      videoTag.currentTime = videoTag.duration * percentScrolled;
    }
    requestAnimationFrame(scrollVideo);
  };
  requestAnimationFrame(scrollVideo);
};

export const getVideoSources = (url) => {
  const { assetHostTCP } = getAPIConfig();
  const urlVal = isAbsoluteUrl(url) ? removeExtension(cleanUpUrl(url)) : url;
  const assetHostVal = isAbsoluteUrl(url) ? getAssetHostVal(url, assetHostTCP) : assetHostTCP;
  const assetHost = assetHostVal.replace('/image/', '/video/');
  const renditions = [{ xform: 'f_mp4,vc_auto,q_auto', type: 'video/mp4', extn: 'mp4' }];
  return renditions.map((rendition) => {
    return {
      url: `${assetHost}/${rendition.xform}/${urlVal}.${rendition.extn}`,
      type: rendition.type,
    };
  });
};

const getPlayIcon = (isModule) =>
  isModule ? `${getImageFilePath()}/play-accessible.svg` : getIconPath('icon-play');

const getPauseIcon = (isModule) =>
  isModule ? `${getImageFilePath()}/pause-accessible.svg` : getIconPath('icon-pause');

const getMuteIcon = (isModule) => isModule && `${getImageFilePath()}/mute.svg`;

const getUnmuteIcon = (isModule) => isModule && `${getImageFilePath()}/unmute.svg`;

const RenderMuteIcon = (playing, toggleMute, muteIconPath) => {
  return (
    playing && (
      <button
        className="video-mute-btn"
        type="button"
        title="Mute Video"
        aria-disabled="false"
        onClick={(e) => {
          e.preventDefault();
          toggleMute();
        }}
      >
        <img className="mute_button_icon" alt="" aria-hidden="true" src={muteIconPath} />
      </button>
    )
  );
};

const callToggleMute = ({
  isMuted,
  videoRef,
  setIsMuted,
  setMuteIconPath,
  muteIcon,
  unmuteIcon,
}) => {
  if (isMuted === true) {
    // eslint-disable-next-line no-param-reassign
    videoRef.current.muted = false;
    setIsMuted(false);
    setMuteIconPath(unmuteIcon);
  } else {
    // eslint-disable-next-line no-param-reassign
    videoRef.current.muted = true;
    setIsMuted(true);
    setMuteIconPath(muteIcon);
  }
};

const getDefaultMuteIcon = (muted, muteIcon, unmuteIcon) =>
  parseBoolean(muted) ? muteIcon : unmuteIcon;

const RenderVideoPoster = ({ videoData, preloadImage, aspectRatioObj, isProductData }) => {
  const videoUrls = getVideoUrls(videoData, undefined, undefined, isProductData);
  const posterUrls = getPosterUrls(videoData);
  const mobilePoster = findPosterImage(posterUrls, videoUrls, 'mobile');
  const desktopPoster = findPosterImage(posterUrls, videoUrls, 'desktop');
  const defaultAspectRatioObj = getAspectRatio(null, videoData, null);
  return (
    <>
      <picture>
        <ImageSource
          srcSet={[{ url: desktopPoster }]}
          media="(min-width: 768px)"
          preloadImage={preloadImage}
        />
        <ImageSource
          srcSet={[{ url: mobilePoster }]}
          media="(max-width: 767px)"
          preloadImage={preloadImage}
        />
        <StyledImage
          src={mobilePoster}
          alt={videoData.title}
          aspectRatioObj={{ ...defaultAspectRatioObj, ...aspectRatioObj }}
        />
      </picture>
    </>
  );
};

RenderVideoPoster.propTypes = {
  videoData: PropTypes.shape({
    autoplay: PropTypes.string,
    muted: PropTypes.string,
    controls: PropTypes.string,
    playsinline: PropTypes.string,
    loop: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
  preloadImage: PropTypes.bool,
  aspectRatioObj: PropTypes.shape({}),
};

RenderVideoPoster.defaultProps = {
  preloadImage: false,
  aspectRatioObj: {},
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const RenderVideo = (props) => {
  const {
    videoData,
    isModule,
    onScrollIdentifier,
    preloadImage,
    makePositionAbsolute,
    isEnableMute,
    className,
    isProductData,
  } = props;
  const [containerRef, isVisible] = useIntersectionObserver({
    threshold: [0.25, 0.75],
    rootMargin: '0px',
  });
  const playIcon = getPlayIcon(isModule);
  const pauseIcon = getPauseIcon(isModule);

  const videoUrls = getVideoUrls(videoData, undefined, undefined, isProductData);
  const {
    autoplay = true,
    playsinline = true,
    muted = true,
    controls = false,
    loop = true,
    title = 'Video',
  } = videoData;
  const { onscroll_animation: onScrollAnimation = false } = videoData;

  const booleanValues = {
    autoPlay: parseBoolean(autoplay),
    playsInline: parseBoolean(playsinline),
    muted: parseBoolean(muted),
    controls: parseBoolean(controls),
    loop: parseBoolean(loop),
    onScrollAnimation: parseBoolean(onScrollAnimation),
  };

  const [iconPath, setIconPath] = useState(pauseIcon);
  const muteIcon = getMuteIcon(isModule);
  const unmuteIcon = getUnmuteIcon(isModule);
  const defaultMuteIcon = getDefaultMuteIcon(muted, muteIcon, unmuteIcon);
  const [muteIconPath, setMuteIconPath] = useState(defaultMuteIcon);
  const [isMuted, setIsMuted] = useState(parseBoolean(muted));
  const [videoLoaded, setVideoLoaded] = useState(false);

  const [sources, setSources] = useState([]);
  const [playing, setPlaying] = useState(true);
  const posterUrls = getPosterUrls(videoData);
  const mobilePoster = findPosterImage(posterUrls, videoUrls, 'mobile');

  const [poster, setPoster] = useState(mobilePoster);
  const desktopPoster = findPosterImage(posterUrls, videoUrls, 'desktop');
  const aspectRatioObj = getAspectRatio(null, videoData, null);
  const videoRef = useRef(null);

  const handlePlayPause = useCallback(() => {
    if (isVisible && booleanValues.autoPlay && playing) {
      setIconPath(pauseIcon);
      videoRef.current.play();
    } else {
      setIconPath(playIcon);
      videoRef.current.pause();
    }
  }, [isVisible]);

  const updateVideoLoaded = () => {
    setVideoLoaded(true);
  };

  useEffect(() => {
    handlePlayPause();
  }, [isVisible, handlePlayPause]);

  const toggleVideo = () => {
    if (playing === true) {
      setIconPath(playIcon);
      videoRef.current.pause();
      setPlaying(false);
    } else {
      setIconPath(pauseIcon);
      videoRef.current.play();
      setPlaying(true);
    }
  };

  useEffect(() => {
    if (playing) {
      setIconPath(pauseIcon);
    } else {
      setIconPath(playIcon);
    }
    const handleVisibilityChange = () => {
      const [isWindowVisible] = getHandlerArgs();
      if (!isWindowVisible) {
        setIconPath(playIcon);
        videoRef.current.pause();
      } else if ((isWindowVisible && booleanValues.autoPlay) || isVisible) {
        setIconPath(pauseIcon);
        videoRef.current.play();
      }
    };

    document.addEventListener(visibility?.event, handleVisibilityChange);
    return () => {
      document.removeEventListener(visibility?.event, handleVisibilityChange);
    };
  }, [isVisible]);

  const toggleMute = () => {
    callToggleMute({ isMuted, videoRef, setIsMuted, setMuteIconPath, muteIcon, unmuteIcon });
  };

  useEffect(() => {
    const { isDesktop, isTablet, isMobile } = getViewportInfo();

    if (isDesktop && videoUrls.desktop) {
      setSources(getVideoSources(videoUrls.desktop));
      setPoster(desktopPoster);
    }
    if (isTablet && videoUrls.tablet) {
      setSources(getVideoSources(videoUrls.tablet));
      setPoster(findPosterImage(posterUrls, videoUrls, 'tablet'));
    }
    if (isMobile && videoUrls.mobile) {
      setSources(getVideoSources(videoUrls.mobile));
    }
    if (booleanValues.onScrollAnimation && !isEmpty(onScrollIdentifier)) {
      onScrollAnimationVideo(`#${onScrollIdentifier}`, `#${onScrollIdentifier} video`);
    }
  }, [videoData]);

  return (
    <>
      {videoLoaded ? null : (
        <RenderVideoPoster
          videoData={videoData}
          preloadImage={preloadImage}
          aspectRatioObj={aspectRatioObj}
          isProductData={isProductData}
        />
      )}
      <StyledVideo
        id="video-player-animation"
        isModule={isModule}
        overrideStyle={getVideoStyle(videoData)}
        onScrollIdentifier={onScrollIdentifier}
        ref={containerRef}
        aspectRatioObj={aspectRatioObj}
        makePositionAbsolute={makePositionAbsolute}
        videoLoaded={videoLoaded}
        isNativeVideoPlayer
      >
        <video
          key={poster}
          autoPlay={booleanValues.autoPlay}
          playsInline={booleanValues.playsInline}
          muted={booleanValues.muted}
          controls={false}
          loop={booleanValues.loop}
          poster={poster}
          ref={videoRef}
          title={title}
          className={className}
          onLoadedMetadata={updateVideoLoaded}
          onCanPlayThrough={updateVideoLoaded}
          onCanPlay={updateVideoLoaded}
        >
          {sources?.length > 0 &&
            sources.map((rendition) => {
              return (
                <source
                  src={rendition.url}
                  type={rendition.type}
                  key={`${rendition.url}_${rendition.type}`}
                />
              );
            })}
          <track kind="captions" />
        </video>
        <button
          className="video-play-button"
          type="button"
          title="Play Video"
          aria-disabled="false"
          onClick={toggleVideo}
        >
          <img
            className="tcp_carousel__play_pause_button_icon"
            aria-hidden="true"
            src={iconPath}
            alt=""
          />
        </button>
        {isEnableMute && RenderMuteIcon(playing, toggleMute, muteIconPath)}
      </StyledVideo>
    </>
  );
};

RenderVideo.propTypes = {
  videoData: PropTypes.shape({
    autoplay: PropTypes.string,
    muted: PropTypes.string,
    controls: PropTypes.string,
    playsinline: PropTypes.string,
    loop: PropTypes.string,
    title: PropTypes.string,
    onscroll_animation: PropTypes.string,
  }),
  isModule: PropTypes.bool.isRequired,
  preloadImage: PropTypes.bool,
  makePositionAbsolute: PropTypes.bool,
  isEnableMute: PropTypes.bool,
};

RenderVideo.defaultProps = {
  videoData: {
    autoplay: '1',
    muted: '1',
    controls: '0',
    loop: '1',
    title: 'Video',
    playsinline: '1',
    onscroll_animation: '0',
  },
  preloadImage: false,
  makePositionAbsolute: false,
  isEnableMute: false,
};

export default RenderVideo;
